import React, { useId } from 'react';
import DsSelect from '../DsSelect';
import DsSwitch from '../DsSwitch';
import WorkAvailabilityHelp from './WorkAvailabilityHelp';

type AvailabilityKind = 'work' | 'time_off';
export type AvailabilitySource = 'daily_prompt' | 'manual';

export type WorkAvailabilitySettingsDefaults = {
  defaultStartLocal?: string | null;
  defaultEndLocal?: string | null;
};

export type WorkAvailabilityDraft = {
  kind: AvailabilityKind;
  startsAt: string;
  endsAt: string;
  includeUpcomingTimeOff: boolean;
  upcomingTimeOffStartsAt: string;
  upcomingTimeOffEndsAt: string;
  nextStartsAt: string;
  nextEndsAt: string;
};

export type WorkAvailabilityEntryPayload = {
  kind: AvailabilityKind;
  startsAt: string;
  endsAt: string;
  timezone: string;
  source: AvailabilitySource;
};

type WorkAvailabilityFormProps = {
  draft: WorkAvailabilityDraft;
  onChange: React.Dispatch<React.SetStateAction<WorkAvailabilityDraft>>;
  showNextWork?: boolean;
  disabled?: boolean;
  showUpcomingTimeOff?: boolean;
  preserveWorkDate?: boolean;
};

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;
type SwitchProps = {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
};

const Select = DsSelect as React.ComponentType<SelectProps>;
const Switch = DsSwitch as React.ComponentType<SwitchProps>;

export const WORK_AVAILABILITY_KIND_OPTIONS = [
  { value: 'work', label: '出勤日' },
  { value: 'time_off', label: '休日・休暇' },
];

export const toDateTimeLocalValue = (date: Date | string | number) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const shifted = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 16);
};

export const createDefaultAvailabilityDraft = (
  settings: WorkAvailabilitySettingsDefaults = {},
): WorkAvailabilityDraft => {
  const now = new Date();
  now.setSeconds(0, 0);
  const start = new Date(now);
  if (settings.defaultStartLocal) {
    const [startHour, startMinute] = String(settings.defaultStartLocal).split(':').map(Number);
    start.setHours(Number.isFinite(startHour) ? startHour : now.getHours(), Number.isFinite(startMinute) ? startMinute : now.getMinutes(), 0, 0);
  }
  const end = new Date(start);
  const [endHour, endMinute] = String(settings.defaultEndLocal || '18:00').split(':').map(Number);
  end.setHours(Number.isFinite(endHour) ? endHour : 18, Number.isFinite(endMinute) ? endMinute : 0, 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);
  const upcomingTimeOffStart = new Date(now);
  upcomingTimeOffStart.setDate(upcomingTimeOffStart.getDate() + 1);
  upcomingTimeOffStart.setHours(0, 0, 0, 0);
  const upcomingTimeOffEnd = new Date(upcomingTimeOffStart);
  upcomingTimeOffEnd.setDate(upcomingTimeOffEnd.getDate() + 1);
  return {
    kind: 'work',
    startsAt: toDateTimeLocalValue(start),
    endsAt: toDateTimeLocalValue(end),
    includeUpcomingTimeOff: false,
    upcomingTimeOffStartsAt: toDateTimeLocalValue(upcomingTimeOffStart),
    upcomingTimeOffEndsAt: toDateTimeLocalValue(upcomingTimeOffEnd),
    nextStartsAt: '',
    nextEndsAt: '',
  };
};

export const availabilityDayLabel = (value: string, fallback = '指定日') => {
  if (!value) return fallback;
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = value.slice(0, 10);
  if (day === toDateTimeLocalValue(today).slice(0, 10)) return '今日';
  if (day === toDateTimeLocalValue(tomorrow).slice(0, 10)) return '明日';
  return '指定日';
};

const datePart = (value: string) => String(value || '').slice(0, 10);
const timePart = (value: string) => String(value || '').slice(11, 16);
const addLocalDays = (day: string, amount: number) => {
  const value = new Date(`${day}T12:00`);
  value.setDate(value.getDate() + amount);
  return toDateTimeLocalValue(value).slice(0, 10);
};
const fixedDateTime = (day: string, time: string) => day && time ? `${day}T${time}` : '';
const endDayOffset = (start: string, end: string) =>
  datePart(start) && datePart(end) && datePart(start) !== datePart(end) ? 1 : 0;
const inferredEndDayOffset = (startTime: string, endTime: string) => startTime && endTime && endTime <= startTime ? 1 : 0;

export const draftToEntryPayload = (
  draft: WorkAvailabilityDraft,
  timezone: string,
  source: AvailabilitySource = 'manual',
): WorkAvailabilityEntryPayload => ({
  kind: draft.kind,
  startsAt: new Date(draft.startsAt).toISOString(),
  endsAt: new Date(draft.endsAt).toISOString(),
  timezone,
  source,
});

export const draftToEntryPayloads = (
  draft: WorkAvailabilityDraft,
  timezone: string,
  source: AvailabilitySource = 'manual',
): WorkAvailabilityEntryPayload[] => {
  if (!draft.startsAt || !draft.endsAt) throw new Error('開始日時と終了日時を指定してください。');
  const entries = [draftToEntryPayload(draft, timezone, source)];
  if (draft.kind === 'work' && draft.includeUpcomingTimeOff) {
    if (!draft.upcomingTimeOffStartsAt || !draft.upcomingTimeOffEndsAt) {
      throw new Error('休みの開始と、休みが明ける日時を指定してください。');
    }
    entries.push({
      kind: 'time_off',
      startsAt: new Date(draft.upcomingTimeOffStartsAt).toISOString(),
      endsAt: new Date(draft.upcomingTimeOffEndsAt).toISOString(),
      timezone,
      source,
    });
  }
  if (draft.nextStartsAt) {
    if (!draft.nextEndsAt) throw new Error('追加する勤務日の終業も指定してください。');
    entries.push({
      kind: 'work',
      startsAt: new Date(draft.nextStartsAt).toISOString(),
      endsAt: new Date(draft.nextEndsAt).toISOString(),
      timezone,
      source,
    });
  }
  return entries;
};

export default function WorkAvailabilityForm({ draft, onChange, showNextWork = true, disabled = false, showUpcomingTimeOff = true, preserveWorkDate = false }: WorkAvailabilityFormProps) {
  const kindId = useId();
  const nextDay = draft.kind === 'time_off' || draft.includeUpcomingTimeOff ? '休み明け' : availabilityDayLabel(draft.nextStartsAt, '明日');
  const setField = <Field extends keyof WorkAvailabilityDraft>(field: Field, value: WorkAvailabilityDraft[Field]) => {
    onChange({ ...draft, [field]: value });
  };
  const setFixedStart = (startField: 'startsAt' | 'nextStartsAt', endField: 'endsAt' | 'nextEndsAt', day: string, time: string) => {
    const end = draft[endField];
    onChange({ ...draft, [startField]: fixedDateTime(day, time), [endField]: end ? fixedDateTime(addLocalDays(day, inferredEndDayOffset(time, timePart(end))), timePart(end)) : '' });
  };
  const setFixedEnd = (startField: 'startsAt' | 'nextStartsAt', endField: 'endsAt' | 'nextEndsAt', day: string, time: string, offset?: number) => {
    const resolvedOffset = offset ?? inferredEndDayOffset(timePart(draft[startField]), time);
    setField(endField, fixedDateTime(addLocalDays(day, resolvedOffset), time));
  };
  const todayDay = preserveWorkDate && datePart(draft.startsAt) ? datePart(draft.startsAt) : toDateTimeLocalValue(new Date()).slice(0, 10);
  const tomorrowDay = addLocalDays(todayDay, 1);
  const fixedTomorrow = draft.kind === 'work' && !draft.includeUpcomingTimeOff;
  return (
    <div className="work-availability__form">
      <div className="work-availability__field">
        <div className="work-availability__label-help">
          <label htmlFor={kindId}>{availabilityDayLabel(draft.startsAt)}の予定</label>
          <WorkAvailabilityHelp topic="period" />
        </div>
        <Select
          id={kindId}
          aria-label={`${availabilityDayLabel(draft.startsAt)}の予定`}
          value={draft.kind}
          disabled={disabled}
          onChange={(event) => {
            const kind = event.target.value as AvailabilityKind;
            if (kind !== 'work') return setField('kind', kind);
            const start = fixedDateTime(todayDay, timePart(draft.startsAt));
            const end = fixedDateTime(addLocalDays(todayDay, endDayOffset(draft.startsAt, draft.endsAt)), timePart(draft.endsAt));
            onChange({ ...draft, kind, startsAt: start, endsAt: end });
          }}
        >
          {WORK_AVAILABILITY_KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
      </div>
      <fieldset className="work-availability__period" aria-label={draft.kind === 'work' ? '勤務時間' : '休日の期間'}>
        <label className="work-availability__field">
          <span>{draft.kind === 'work' ? '始業' : '休みの開始'}</span>
          <input
            className="work-availability__input"
            type={draft.kind === 'work' ? 'time' : 'datetime-local'}
            value={draft.kind === 'work' ? timePart(draft.startsAt) : draft.startsAt}
            disabled={disabled}
            onChange={(event) => draft.kind === 'work' ? setFixedStart('startsAt', 'endsAt', todayDay, event.target.value) : setField('startsAt', event.target.value)}
          />
        </label>
        <div className="work-availability__field">
          <span>{draft.kind === 'work' ? '終業' : '休みが明ける日時'}</span>
          {draft.kind === 'work' ? <Select aria-label="終業日" value={endDayOffset(draft.startsAt, draft.endsAt)} disabled={disabled} onChange={(event) => setFixedEnd('startsAt', 'endsAt', todayDay, timePart(draft.endsAt), Number(event.target.value))}>
            <option value={0}>当日</option><option value={1}>翌日</option>
          </Select> : null}
          <input
            className="work-availability__input"
            aria-label={draft.kind === 'work' ? '終業' : '休みが明ける日時'}
            type={draft.kind === 'work' ? 'time' : 'datetime-local'}
            value={draft.kind === 'work' ? timePart(draft.endsAt) : draft.endsAt}
            disabled={disabled}
            onChange={(event) => draft.kind === 'work' ? setFixedEnd('startsAt', 'endsAt', todayDay, event.target.value) : setField('endsAt', event.target.value)}
          />
        </div>
      </fieldset>
      {draft.kind === 'work' && showUpcomingTimeOff ? (
        <>
          <div className="work-availability__switch-row">
            <span className="work-availability__label-help">
              <span>明日からお休み</span>
              <WorkAvailabilityHelp topic="timeOff" />
            </span>
            <Switch
              checked={Boolean(draft.includeUpcomingTimeOff)}
              disabled={disabled}
              onChange={(includeUpcomingTimeOff) => setField('includeUpcomingTimeOff', includeUpcomingTimeOff)}
              ariaLabel="明日からお休み"
            />
          </div>
          {draft.includeUpcomingTimeOff ? (
            <fieldset className="work-availability__period" aria-label="終業後の休日の期間">
              <legend>お休みの期間</legend>
              <label className="work-availability__field">
                <span>休みの開始</span>
                <input className="work-availability__input" type="datetime-local" value={draft.upcomingTimeOffStartsAt} disabled={disabled} onChange={(event) => setField('upcomingTimeOffStartsAt', event.target.value)} />
              </label>
              <label className="work-availability__field">
                <span>休みが明ける日時</span>
                <input className="work-availability__input" type="datetime-local" value={draft.upcomingTimeOffEndsAt} disabled={disabled} onChange={(event) => setField('upcomingTimeOffEndsAt', event.target.value)} />
              </label>
            </fieldset>
          ) : null}
        </>
      ) : null}
      {showNextWork ? (
        <fieldset className="work-availability__period" aria-label="次回勤務時間">
          <legend>{nextDay}の予定（任意）</legend>
          <label className="work-availability__field">
            <span>{nextDay}の始業（任意）</span>
            <input
              className="work-availability__input"
              type={fixedTomorrow ? 'time' : 'datetime-local'}
              value={fixedTomorrow ? timePart(draft.nextStartsAt) : draft.nextStartsAt}
              disabled={disabled}
              onChange={(event) => fixedTomorrow ? setFixedStart('nextStartsAt', 'nextEndsAt', tomorrowDay, event.target.value) : setField('nextStartsAt', event.target.value)}
            />
          </label>
          <div className="work-availability__field">
            <span>{nextDay}の終業（任意）</span>
            {fixedTomorrow ? <Select aria-label="明日の終業日" value={endDayOffset(draft.nextStartsAt, draft.nextEndsAt)} disabled={disabled || !draft.nextStartsAt} onChange={(event) => setFixedEnd('nextStartsAt', 'nextEndsAt', tomorrowDay, timePart(draft.nextEndsAt), Number(event.target.value))}>
              <option value={0}>当日</option><option value={1}>翌日</option>
            </Select> : null}
            <input
              className="work-availability__input"
              aria-label={`${nextDay}の終業（任意）`}
              type={fixedTomorrow ? 'time' : 'datetime-local'}
              value={fixedTomorrow ? timePart(draft.nextEndsAt) : draft.nextEndsAt}
              disabled={disabled || !draft.nextStartsAt}
              onChange={(event) => fixedTomorrow ? setFixedEnd('nextStartsAt', 'nextEndsAt', tomorrowDay, event.target.value) : setField('nextEndsAt', event.target.value)}
            />
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}
